import * as THREE from 'three';

const BASE_DURATION = 1.35;
const BASE_DROP_HEIGHT = 14.0;

function clamp01(v) {
  return Math.max(0, Math.min(1, v));
}

function easeOutCubic(t) {
  const k = clamp01(t);
  return 1 - ((1 - k) ** 3);
}

function easeInCubic(t) {
  const k = clamp01(t);
  return k ** 3;
}

function makeMeshMaterial(color, opacity) {
  return new THREE.MeshBasicMaterial({
    color,
    transparent: true,
    opacity,
    depthWrite: false,
    depthTest: true,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
  });
}

export class EnemyDropPreviewSystem {
  constructor(scene) {
    this.scene = scene;
    this.items = [];
  }

  spawnAt(x, z, opts = {}) {
    const radius = Math.max(0.4, opts.radius ?? 1.35);
    const y = opts.y ?? 0.02;
    const duration = Math.max(0.35, opts.duration ?? BASE_DURATION);
    const dropHeight = Math.max(3.5, opts.dropHeight ?? BASE_DROP_HEIGHT);

    const group = new THREE.Group();
    group.position.set(x, y, z);

    const landingRing = new THREE.Mesh(
      new THREE.RingGeometry(radius * 0.78, radius, 48),
      makeMeshMaterial(0xff8b3d, 0.0),
    );
    landingRing.rotation.x = -Math.PI / 2;
    landingRing.renderOrder = 24;

    const landingGlow = new THREE.Mesh(
      new THREE.CircleGeometry(radius * 0.62, 40),
      makeMeshMaterial(0xffe8b8, 0.0),
    );
    landingGlow.rotation.x = -Math.PI / 2;
    landingGlow.position.y = 0.005;
    landingGlow.renderOrder = 23;

    const rope = new THREE.Mesh(
      new THREE.CylinderGeometry(0.022, 0.022, dropHeight, 8, 1, true),
      makeMeshMaterial(0xa7d8ff, 0.0),
    );
    rope.position.y = dropHeight * 0.5;
    rope.renderOrder = 22;

    const descender = new THREE.Mesh(
      new THREE.SphereGeometry(radius * 0.24, 14, 10),
      makeMeshMaterial(0xffb45a, 0.0),
    );
    descender.position.y = dropHeight;
    descender.renderOrder = 25;

    group.add(landingGlow);
    group.add(landingRing);
    group.add(rope);
    group.add(descender);
    this.scene.add(group);

    const item = {
      group,
      landingRing,
      landingGlow,
      rope,
      descender,
      duration,
      radius,
      dropHeight,
      life: 0,
      onComplete: typeof opts.onComplete === 'function' ? opts.onComplete : null,
    };
    this.items.push(item);
    return item;
  }

  triggerAtPlayer(player) {
    if (!player) return null;
    return this.spawnAt(player.x, player.z);
  }

  clear() {
    for (let i = this.items.length - 1; i >= 0; i -= 1) {
      this._disposeItem(this.items[i]);
    }
    this.items.length = 0;
  }

  _disposeItem(item) {
    this.scene.remove(item.group);
    item.group.traverse((obj) => {
      if (obj.material?.dispose) obj.material.dispose();
      if (obj.geometry?.dispose) obj.geometry.dispose();
    });
  }

  update(dt) {
    if (!this.items.length) return;
    for (let i = this.items.length - 1; i >= 0; i -= 1) {
      const item = this.items[i];
      item.life += dt;
      const t = clamp01(item.life / item.duration);

      const descendT = easeInCubic(t);
      const markerT = easeOutCubic(Math.min(1, t / 0.7));
      const fadeOutT = clamp01((t - 0.82) / 0.18);

      const y = THREE.MathUtils.lerp(item.dropHeight, 0.06, descendT);
      item.descender.position.y = y;

      const ropeLen = Math.max(0.24, y);
      item.rope.scale.y = ropeLen / item.dropHeight;
      item.rope.position.y = ropeLen * 0.5;

      const pulse = 0.8 + Math.sin(item.life * 18) * 0.2;
      item.landingRing.scale.setScalar(0.76 + markerT * 0.56);
      item.landingGlow.scale.setScalar(0.62 + markerT * 0.46);
      item.landingRing.material.opacity = (0.28 + markerT * 0.68) * (1 - fadeOutT);
      item.landingGlow.material.opacity = (0.12 + markerT * 0.36) * (1 - fadeOutT * 0.75) * pulse;
      item.rope.material.opacity = (0.24 + markerT * 0.56) * (1 - fadeOutT);
      item.descender.material.opacity = (0.32 + markerT * 0.68) * (1 - fadeOutT * 0.88);

      if (t >= 1) {
        if (item.onComplete) {
          item.onComplete({
            x: item.group.position.x,
            y: item.group.position.y,
            z: item.group.position.z,
            radius: item.radius,
          });
        }
        this._disposeItem(item);
        this.items.splice(i, 1);
      }
    }
  }
}

